import {
  DEFAULT_IMAGE,
  MEDIA_QUERY_DEVICE_SIZE,
  WIDTH_DESCRIPTORS,
} from '@/app/components/media/image/constants';
import type {
  ImageLoadingType,
  ImageOptions,
  ImageRenderOption,
  ImageSource,
} from '@/app/components/media/image/types';

export const isSvg = (src: string = ''): boolean => {
  return src?.startsWith('data:image/svg') || src?.endsWith('.svg');
};

export const getImageQueryParams = (
  src: string = '',
  options: ImageOptions = {},
  as: ImageRenderOption
): string => {
  const {
    width,
    height,
    crop,
    fit,
    trim,
    blur,
    quality = 75,
    dpr = 1,
  } = options;

  if (
    !src ||
    src === DEFAULT_IMAGE ||
    src.startsWith('data:image/') ||
    isSvg(src)
  ) {
    return src;
  }

  const imageParams = new URLSearchParams({
    format: 'pjpg',
    auto: 'webp',
    quality: `${quality}`,
  });
  if (width) imageParams.set('width', `${width}`);
  if (height) imageParams.set('height', `${height}`);
  if (crop) imageParams.set('crop', `${crop}`);
  if (fit) imageParams.set('fit', `${fit}`);
  if (trim) imageParams.set('trim', `${trim}`);
  if (blur) imageParams.set('blur', `${blur}`);
  if (as === 'sizes') {
    return `${src}?${imageParams.toString()}`;
  }

  if (width && width >= MEDIA_QUERY_DEVICE_SIZE.desktop_xl) {
    imageParams.set('dpr', '1.5');
  } else if (dpr > 1) {
    imageParams.set('dpr', `${dpr}`);
  }
  return `${src}?${imageParams.toString()}`;
};

export const getImgSrcset = (
  src: string,
  options: ImageOptions = {},
  inView?: boolean,
  loading?: ImageLoadingType
): string | undefined => {
  if (!src || (loading === 'lazy' && !inView) || isSvg(src)) return undefined;

  return WIDTH_DESCRIPTORS.map((width) => {
    const url = getImageQueryParams(src, { ...options, width }, 'sizes');
    return `${url} ${width}w`;
  }).join(', ');
};

export const getSourceSrcset = ({
  srcset,
  options,
  media,
}: ImageSource): string => {
  if (!srcset) return '';

  if (isSvg(srcset)) return srcset;

  const srcset1x = getImageQueryParams(
    srcset,
    { ...options, dpr: 1.5 },
    'sources'
  );
  const srcset2x = getImageQueryParams(
    srcset,
    { ...options, dpr: 2 },
    'sources'
  );
  let srcsetString = `${srcset1x} 1x, ${srcset2x} 2x`;

  if (
    media &&
    parseInt(`${media.maxWidth}`) < MEDIA_QUERY_DEVICE_SIZE.mobile_md
  ) {
    const srcset3x = getImageQueryParams(
      srcset,
      { ...options, dpr: 2.5 },
      'sources'
    );
    srcsetString += `, ${srcset3x} 3x`;
  }

  return srcsetString;
};

export const getMediaCondition = (source: ImageSource): string => {
  const { media, srcset } = source;
  if (!media) return '';

  if (!isSvg(srcset)) {
    const conditions: string[] = [];
    if (media?.minWidth) conditions.push(`(min-width: ${media.minWidth}px)`);
    if (media?.maxWidth) conditions.push(`(max-width: ${media.maxWidth}px)`);
    if (media?.orientation)
      conditions.push(`(orientation: ${media.orientation})`);
    return conditions.join(' and ');
  }

  if (!!media.device) {
    return media.device === 'desktop'
      ? `(min-width: ${media.minWidth}px)`
      : `(max-width: ${media.maxWidth}px)`;
  }

  // If there's no device information, it will pick minWidth or maxWidth value.
  let result = '';
  if (media?.minWidth) {
    result = `(min-width: ${media.minWidth}px)`;
  }
  // maxWidth has more priority because mobile has more user
  if (media?.maxWidth) {
    result = `(max-width: ${media.maxWidth}px)`;
  }
  return result;
};

export const getAdjustedImageSrc = (
  src: string,
  defaultSrc: string,
  inView: boolean,
  loading: ImageLoadingType
): string => {
  if (!src) return defaultSrc;

  return loading === 'eager' || inView ? src : defaultSrc;
};
